import { useMemo, useRef, useState, useEffect } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type FieldPath,
  type RegisterOptions,
} from "react-hook-form";
import { useTheme } from "../../contexts/ThemeContext";

export type ComboBoxOption = { value: number; label: string };

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  options: ComboBoxOption[];
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  loading?: boolean;
  rules?: RegisterOptions<T, FieldPath<T>>;
  /**
   * Nombre de caractères à saisir avant que la liste ne propose quelque chose.
   * 0 affiche tout dès l'ouverture. Utile sur un référentiel volumineux, où une liste
   * complète n'aide pas : c'est le seuil de 3 retenu par Ikki sur ce même référentiel.
   */
  minChars?: number;
  /** Appelé quand l'utilisateur choisit une option (pas au simple filtrage). */
  onPick?: (option: ComboBoxOption) => void;
  /**
   * Notifie le texte saisi. Fourni quand la recherche est distante : le composant cesse
   * alors de filtrer lui-même et affiche `options` telles quelles, le parent étant
   * responsable de les faire correspondre au texte.
   */
  onQueryChange?: (query: string) => void;
  /** Message affiché à la place de la liste (erreur de recherche, par exemple). */
  message?: string;
};

const MAX_VISIBLE = 50;

/**
 * Liste déroulante avec auto-complétion, branchée sur react-hook-form.
 * La valeur du formulaire est l'identifiant (number) ou "" si rien n'est choisi ; le
 * texte saisi n'est qu'un filtre, jamais la valeur soumise. Un texte qui ne correspond
 * à aucune option laisse donc le champ vide, et la règle `required` le signale.
 *
 * Deux modes de recherche :
 * - sans `onQueryChange`, le composant filtre lui-même la liste complète qu'on lui donne ;
 * - avec `onQueryChange`, il délègue : il remonte le texte saisi et affiche `options` sans
 *   les retoucher. C'est le mode utilisé pour les sociétés, dont le référentiel n'est pas
 *   dans wapagri et n'est interrogeable que par recherche via Milo.
 *
 * Même principe que TextField : pas de <input> brut dans les écrans.
 */
export function ComboBoxField<T extends FieldValues>({
  control, name, options, label, placeholder, autoFocus, loading, rules, minChars = 0,
  onPick, onQueryChange, message,
}: Props<T>) {
  const { t } = useTheme();
  const { field, fieldState } = useController({ control, name, rules });
  const error = fieldState.error?.message;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Mémorise l'option choisie. Indispensable en recherche distante : choisir remet le
  // texte cherché à vide, donc `options` se vide aussi, et le libellé ne serait plus
  // retrouvable. On ne s'en sert que si elle correspond encore à la valeur du formulaire,
  // pour qu'un reset externe du champ ne laisse pas un libellé fantôme.
  const [picked, setPicked] = useState<ComboBoxOption | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => {
    const value = Number(field.value);
    const fromOptions = options.find((o) => o.value === value);
    if (fromOptions) return fromOptions;
    if (picked && picked.value === value) return picked;
    return null;
  }, [options, field.value, picked]);

  // Le texte affiché suit la sélection quand le champ est fermé : après un choix, on
  // veut voir le libellé complet et non le filtre qui a servi à le trouver.
  const text = open ? query : selected?.label ?? "";

  const trimmed = query.trim();
  const tooShort = trimmed.length < minChars;

  const remote = onQueryChange != null;

  const filtered = useMemo(() => {
    if (tooShort) return [];
    // En recherche distante, le filtrage a déjà été fait par le serveur : re-filtrer ici
    // masquerait des résultats légitimes (Milo cherche sur la société entière, pas
    // forcément sur le libellé exactement tel qu'affiché).
    if (remote) return options.slice(0, MAX_VISIBLE);
    const q = trimmed.toLowerCase();
    const base = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return base.slice(0, MAX_VISIBLE);
  }, [options, trimmed, tooShort, remote]);

  const setQueryAndNotify = (value: string) => {
    setQuery(value);
    onQueryChange?.(value);
  };

  // Fermeture au clic à l'extérieur : sans cela la liste reste ouverte par-dessus le reste.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (option: ComboBoxOption) => {
    field.onChange(option.value);
    setPicked(option);
    setQueryAndNotify("");
    setOpen(false);
    onPick?.(option);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      // Empêche la soumission du formulaire quand la liste est ouverte.
      if (open && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      {label && (
        <label
          htmlFor={field.name}
          style={{ display: "block", fontSize: 12, color: t.text2, marginBottom: 6, fontWeight: 500 }}
        >
          {label}{rules?.required && <span style={{ color: t.lateColor }}> *</span>}
        </label>
      )}
      <input
        id={field.name}
        name={field.name}
        ref={field.ref}
        value={text}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(e) => { setQueryAndNotify(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => { setQueryAndNotify(""); setOpen(true); setHighlight(0); }}
        onBlur={field.onBlur}
        onKeyDown={onKeyDown}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 8,
          border: `1px solid ${error ? t.lateColor : t.inputBorder}`,
          background: t.inputBg, color: t.text1,
          fontSize: 14, fontFamily: "inherit", outline: "none",
        }}
      />

      {open && (
        <div style={{
          position: "absolute", zIndex: 20, left: 0, right: 0, top: "100%", marginTop: 4,
          background: t.cardBg, border: `1px solid ${t.rowBorder}`, borderRadius: 8,
          maxHeight: 260, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
        }}>
          {tooShort && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: t.text2 }}>
              Saisir au moins {minChars} caractères
            </div>
          )}
          {!tooShort && message && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: t.lateColor }}>{message}</div>
          )}
          {!tooShort && !message && loading && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: t.text2 }}>Recherche...</div>
          )}
          {!tooShort && !message && !loading && filtered.length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: t.text2 }}>Aucun résultat</div>
          )}
          {filtered.map((o, i) => (
            <div
              key={o.value}
              // onMouseDown et non onClick : le blur de l'input se déclenche avant le clic
              // et fermerait la liste avant que la sélection ne soit prise en compte.
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "9px 12px", fontSize: 13.5, cursor: "pointer",
                background: i === highlight ? t.rowSel : "transparent",
                color: t.text1,
              }}
            >
              {o.label}
            </div>
          ))}
          {!remote && !tooShort && options.length > filtered.length && trimmed === "" && (
            <div style={{ padding: "8px 12px", fontSize: 11.5, color: t.text2, borderTop: `1px solid ${t.rowBorder}` }}>
              {options.length} entrées, affinez la recherche
            </div>
          )}
        </div>
      )}

      {error && <div style={{ fontSize: 11.5, color: t.lateColor, marginTop: 5 }}>{error}</div>}
    </div>
  );
}
